<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col v-if="isFetching || !mapPanelDataFetched">
      <main-loader class="mt-10" />
    </v-col>
    <v-col v-else>
      <card-title-row
        translation-path="sidebar.ZDMfailureReport.CardTitle"
        class="pt-2"
        :has-back-button="true"
        @action="goBack"
      ></card-title-row>
      <sidebar-row>
        <italic-subheader translation-path="sidebar.ZDMfailureReport.CardSubtitle" class="pb-4 text-left" />
      </sidebar-row>
      <sidebar-form
        autocomplete="off"
        :is-valid.sync="isFormValid"
        ref="form"
        v-model="model"
        :inputs="inputs"
        is-editing-on
      >
        <template #reported_at>
          {{ currentTime }}
        </template>
        <template #reported_by>
          {{ currentUser.name }}
        </template>
        <template #pickDescription>
          <data-input
            :data-type="{
              name: 'select',
            }"
            v-model="short_description"
            :items="descriptions"
            @input="full_description = $event.description"
            dense
            outlined
            item-text="dict_value"
            return-object
            style="max-width: 16vw"
          ></data-input>
        </template>
        <template #or>
          <div class="text-left text-body-2">{{ $i18n.t('default.or').toLowerCase() }}</div>
        </template>
        <template #description>
          <v-textarea v-model="full_description" auto-grow outlined rows="3" row-height="30"></v-textarea>
        </template>
        <template #lantern="scope">
          <sidebar-objects-input
            property-name="number"
            property-translation=""
            :property-datatype="{ name: 'text' }"
            :property-dots-object="{ name: 'addByNumber' }"
            data-source-scope="zdm_data"
            v-bind="scope"
            v-model="lantern"
            :allowed-layers-ids="zdmLayersArray"
            translation-path="sidebar.ZDMfailureReport.geometrySectionTitle"
          >
          </sidebar-objects-input>
        </template>
        <template #street="scope">
          <data-input :data-type="{ name: 'text' }" v-model="street" v-bind="scope"></data-input>
        </template>
        <template #district="scope">
          <data-input :data-type="{ name: 'text' }" v-model="district" v-bind="scope"></data-input>
        </template>
        <template #pickAttachments>
          <v-file-input outlined dense v-model="files" multiple>
            <template v-slot:selection="{ text }">
              <v-chip small label color="primary">
                {{ text }}
              </v-chip>
            </template>
          </v-file-input>
        </template>
        <template #sendToMe>
          <v-tooltip :disabled="Boolean(currentUser.email)" top color="font">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-checkbox class="py-0 my-0" v-model="model.sendToMe" :disabled="!Boolean(currentUser.email)">
                  <template #label>
                    <v-row
                      :style="`color: ${currentUser.email ? 'rgba(0, 0, 0, 0.87)' : ''}; font-size: 14px`"
                      no-gutters
                    >
                      {{ $i18n.t('sidebar.ZDMfailureReport.sendToMe') }}
                    </v-row>
                  </template>
                </v-checkbox>
              </span>
            </template>
            <span>{{ $i18n.t('tooltips.noEmailAddress') }}</span>
          </v-tooltip>
        </template>
      </sidebar-form>
    </v-col>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarForm from '@/components/SidebarForm';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import SidebarObjectsInput from '@/components/SidebarObjectsInput';
import DataInput from '@/components/DataInput';

export default {
  name: 'ZDMFailureReport',
  components: {
    DataInput,
    SidebarObjectsInput,
    SidebarRow,
    MainLoader,
    SidebarForm,
    ItalicSubheader,
    CardTitleRow,
  },
  data: () => ({
    model: {},
    full_description: '',
    street: '',
    district: '',
    short_description: undefined,
    isFetching: false,
    isFormValid: false,
    currentTime: new Date().toLocaleTimeString(),
    lantern: [],
    files: [],
  }),
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    currentUser: get('users/currentUser'),
    users: get('users/users'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    descriptions: get('zdm/failureDescriptions'),
    zdmLayers: get('admin/modulesMapping@zdm_data.layers'),
    zdmLayersArray() {
      return this.$_objectToArray(this.zdmLayers);
    },
    inputs() {
      return [
        {
          name: 'reported_at',
          translationPath: 'sidebar.ZDMfailureReport.dueTime',
          'prepend-inner-icon': 'mdi-calendar',
          dataType: { name: 'datetime' },
          outlined: true,
          oneLineInput: false,
        },
        {
          name: 'reported_by',
          translationPath: 'sidebar.ZDMfailureReport.reportedBy',
          placeholder: 'Jan Kowalski',
          hasDivider: true,
          oneLineInput: false,
        },
        {
          name: 'pickDescription',
          sectionTitle: 'sidebar.ZDMfailureReport.descriptionSectionTitle',
          translationPath: 'sidebar.ZDMfailureReport.pickDescription',
          subtitlePath: 'sidebar.ZDMfailureReport.pickDescriptionSubtitle',
          hasSubtitle: true,
          oneLineInput: false,
        },
        {
          name: 'or',
          hasNameColumn: false,
          noPaddingsY: true,
        },
        {
          name: 'description',
          translationPath: 'sidebar.ZDMfailureReport.enterDescription',
          oneLineInput: false,
          hasDivider: true,
        },
        {
          name: 'lantern',
          hasNameColumn: false,
          cols: '11',
          hasDivider: true,
        },
        {
          name: 'street',
          sectionTitle: 'sidebar.ZDMfailureReport.address',
          translationPath: 'sidebar.ZDMfailureReport.streetName',
          oneLineInput: false,
          clearable: true,
        },
        {
          name: 'district',
          translationPath: 'sidebar.ZDMfailureReport.districtName',
          oneLineInput: false,
          hasDivider: true,
          clearable: true,
        },
        {
          name: 'pickAttachments',
          translationPath: 'sidebar.ZDMfailureReport.pickAttachments',
          sectionTitle: 'sidebar.ZDMfailureReport.attachmentsSectionTitle',
          oneLineInput: false,
          hasDivider: true,
        },
        {
          name: 'sendToMe',
          hasNameColumn: false,
          oneLineInput: false,
        },
      ];
    },
    newFailureObject() {
      const dateNow = this.$dayjs();
      const dateNowIsoString = dateNow.format('YYYY-MM-DDTHH:mm[:00.000]');
      return { reported_by: this.currentUser.id, reported_at: dateNowIsoString };
    },
  },
  methods: {
    sendReportEmail: call('zdm/sendReportEmail'),
    getDescriptions: call('zdm/getDescriptions'),
    startTime() {
      this.currentTime = new Date().toLocaleString();
      setTimeout(this.startTime, 1000);
    },
    async agreeEmailSending() {
      this.model = {
        reported_by: this.currentUser.id,
        reported_at: this.$dayjs().format('YYYY-MM-DDTHH:mm[:00.000]'),
        full_description: this.full_description ? this.full_description : '',
        short_description: this.short_description ? this.short_description?.dict_value : '',
        street: this.street,
        district: this.district,
      };
      const payload = {
        model: this.model,
        files: this.files,
        objects: JSON.stringify(this.lantern),
      };
      this.isStickyButtonsLoading = true;
      try {
        await this.sendReportEmail(payload);
        this.isStickyButtonsLoading = false;
        this.clearData();
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.emailSentSuccessfully'),
        });
      } catch {
        this.isStickyButtonsLoading = false;
      }
    },
    clearData() {
      this.full_description = '';
      this.short_description = undefined;
      this.street = '';
      this.district = '';
      this.model = {};
      this.files = [];
      this.lantern = [];
      this.$refs?.form.$refs?.form.resetValidation();
    },
    goBack() {
      this.$router.back();
    },
    async init() {
      this.model = { ...this.newFailureObject };
      this.isFetching = true;
      this.$root.$off('agreeEmailSending');
      this.$root.$on('agreeEmailSending', this.agreeEmailSending);
      this.isStickyButtonsLoading = false;
      this.isStickyButtonsVisible = true;
      this.stickyButtons = [
        {
          action: 'agreeEmailSending',
          translationPath: 'dialog.agreeSend',
        },
      ];
      await this.getDescriptions();
      this.isFetching = false;
    },
    setFailureLamp({ layer_id, id, number, street, district }) {
      this.lantern.push({
        layer_id,
        id,
        number,
        street,
        district,
      });
    },
  },
  watch: {
    isFormValid: {
      immediate: true,
      handler(nV) {
        this.isStickyButtonsValid = nV;
      },
    },
    lantern: {
      immediate: true,
      handler(nV) {
        if (this.street === '' && nV[0]) {
          this.street = nV[0].street;
        } else if (nV.length === 0) {
          this.street = '';
        }
        if (this.district === '' && nV[0]) {
          this.district = nV[0].district;
        } else if (nV.length === 0) {
          this.district = '';
        }
      },
    },
  },
  mounted() {
    this.init();
    this.startTime();
    this.$root.$on('setFailureLamp', this.setFailureLamp);
  },
  beforeDestroy() {
    this.$root.$off('setFailureLamp');
    this.isStickyButtonsVisible = false;
  },
};
</script>
